import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { isApiError, putFile } from '../../api/client';
import { partner } from '../../api/partner';
import type { CaseCard, CaseDocument, Step } from '../../api/types';
import { canWriteCases, useSession } from '../../app/session';
import { toast } from '../../app/toast';
import { ErrorBlock } from '../../components/ErrorBlock';
import { DownloadIcon } from '../../components/Icons';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { PhotoCapture } from '../../components/PhotoCapture';
import { useT } from '../../i18n';
import { openFile } from '../../lib/download';
import { describeError } from '../../lib/errors';
import { formatBytes, localName, useFormat } from '../../lib/format';
import { preparePhoto } from '../../lib/image';
import { useAnnounceScreen } from '../../lib/problemContext';
import { useAsync, useOnVisible } from '../../lib/useAsync';
import { StepPanel } from '../steps/StepPanel';
import { caseRoute } from './CasesPage';

async function soft<T>(p: Promise<T>, empty: T): Promise<T> {
  try {
    return await p;
  } catch (e) {
    if (isApiError(e) && (e.status === 404 || e.status === 403)) return empty;
    throw e;
  }
}

interface CaseData {
  c: CaseCard;
  steps: Step[];
  docs: CaseDocument[];
}

async function loadCase(id: string): Promise<CaseData> {
  const [c, steps, docs] = await Promise.all([
    partner.case(id),
    soft(partner.steps({ kind: 'case', id }).then((r) => r.items), [] as Step[]),
    soft(partner.caseDocuments(id), [] as CaseDocument[]),
  ]);
  return { c, steps, docs };
}

export function CasePage() {
  const { id = '' } = useParams();
  const q = useAsync(() => loadCase(id), [id]);
  useOnVisible(() => void q.reload());
  useAnnounceScreen(`/cases/${id}`, { case_number: q.data?.c.number });
  if (q.loading && !q.data) return <Loading />;
  if (q.error && !q.data) return <ErrorBlock error={q.error} onRetry={() => void q.reload()} />;
  if (!q.data) return null;
  return <CaseView data={q.data} reload={() => void q.reload()} />;
}

function CaseView({ data, reload }: { data: CaseData; reload: () => void }) {
  const t = useT();
  const f = useFormat();
  const me = useSession((s) => s.me);
  const { c, steps, docs } = data;
  const [busy, setBusy] = useState(false);
  const route = caseRoute(c, f.country);
  const transitions = (c.transitions_available ?? []).filter((x) => x.to_key !== 'cancelled');

  async function upload(files: File[]) {
    setBusy(true);
    try {
      for (const file of files) {
        const p = await preparePhoto(file);
        const ticket = await partner.uploadUrl(
          { kind: 'case', id: c.id },
          { document_type_key: p.mime.startsWith('image/') ? 'photo' : 'other', file_name: p.name, mime: p.mime, size: p.blob.size },
        );
        await putFile(ticket.upload_url, p.blob, ticket.headers?.['Content-Type'] ?? p.mime);
        await partner.completeDocument(ticket.document_id);
      }
      toast(t('case.uploaded'));
      reload();
    } catch (e) {
      toast(t(describeError(e)), 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function move(to: string) {
    setBusy(true);
    try {
      await partner.transition(c.id, to);
      toast(t('case.moved'));
      reload();
    } catch (e) {
      toast(t(describeError(e)), 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader back="/cases" title={c.number} sub={c.deceased?.full_name ?? c.deceased_name ?? c.title ?? undefined} />
      <div className="card space-y-1 text-[15px]">
        <p>
          <span className="text-lab">{t('case.stage')}: </span>
          <span className="font-semibold">{localName(c.state?.name, f.locale) || c.state_key}</span>
        </p>
        {route || c.title ? (
          <p>
            <span className="text-lab">{t('case.route')}: </span>
            {c.title || route}
          </p>
        ) : null}
        {c.customer ? (
          <p>
            <span className="text-lab">{t('case.customer')}: </span>
            {c.customer.full_name}
          </p>
        ) : null}
      </div>

      <div className="mt-5">
        <StepPanel target={{ kind: 'case', id: c.id }} steps={steps} onChanged={reload} disabled={!!c.closed_at} />
      </div>

      {transitions.length && canWriteCases(me) ? (
        <>
          <h2 className="sec">{t('case.next')}</h2>
          <div className="space-y-2">
            {transitions.map((x) => (
              <div key={x.to_key}>
                <button type="button" className="btn w-full" disabled={busy || !!x.blocked_reason} onClick={() => void move(x.to_key)}>
                  {localName(x.name, f.locale) || x.to_key}
                </button>
                {x.blocked_reason ? <p className="mt-1 text-[13px] text-lab">{t('case.blocked', { reason: x.blocked_reason })}</p> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      <h2 className="sec">{t('case.docs')}</h2>
      {docs.length ? (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="card tap-row"
                onClick={() => void openFile(() => partner.documentUrl(d.id)).catch((e: unknown) => toast(t(describeError(e)), 'bad'))}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{localName(d.document_type?.name, f.locale) || d.file_name}</span>
                  <span className="block truncate text-[14px] text-lab">
                    {d.file_name} · {formatBytes(d.size)} · {f.date(d.created_at, false)}
                  </span>
                </span>
                <DownloadIcon />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[15px] text-lab">{t('case.noDocs')}</p>
      )}
      {!c.closed_at ? (
        <div className="mt-3">
          <p className="label">{t('case.addDoc')}</p>
          <PhotoCapture onPick={(files) => void upload(files)} disabled={busy} allowPdf />
        </div>
      ) : null}
    </>
  );
}
